import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@/generated/openapi-types";
import { useApi } from "./ApiProvider";

export type Website = components["schemas"]["Website"];
export type Document = components["schemas"]["Document"];
export type AssistantConfig = components["schemas"]["AssistantConfig"];
export type KnowledgeStatus = components["schemas"]["KnowledgeStatusReport"];
type KnowledgeStatusValue = components["schemas"]["KnowledgeStatus"];
export type OnboardingStep = components["schemas"]["OnboardingStep"];

/** Every query throws the API's error body so screens can show its message. */
function unwrap<T>(r: { data?: T; error?: unknown }): T {
  if (r.error !== undefined || r.data === undefined)
    throw r.error ?? new Error("empty response");
  return r.data;
}

export function useMe() {
  const api = useApi();
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.GET("/me")),
  });
}

export function useWebsites() {
  const api = useApi();
  return useQuery({
    queryKey: ["websites"],
    queryFn: async () => unwrap(await api.GET("/websites")).items,
    refetchInterval: (q) =>
      q.state.data?.some(
        (w) =>
          w.sitemapStatus === "pending" || w.sitemapStatus === "discovering",
      )
        ? 2000
        : false,
  });
}

export function useWebsitePages(websiteId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["websites", websiteId, "pages"],
    enabled: websiteId !== null,
    queryFn: async () =>
      unwrap(
        await api.GET("/websites/{websiteId}/pages", {
          params: { path: { websiteId: websiteId ?? "" } },
        }),
      ),
    refetchInterval: (q) =>
      q.state.data?.items.some((p) => isSettling(p.status)) ? 2000 : false,
  });
}

export function useDocuments() {
  const api = useApi();
  return useQuery({
    queryKey: ["documents"],
    queryFn: async () => unwrap(await api.GET("/documents")).items,
    refetchInterval: (q) =>
      q.state.data?.some((d) => isSettling(d.status)) ? 2000 : false,
  });
}

export function useAssistant() {
  const api = useApi();
  return useQuery({
    queryKey: ["assistant"],
    queryFn: async () => unwrap(await api.GET("/assistant")),
  });
}

export function useKnowledgeStatus(poll = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["knowledge"],
    queryFn: async () => unwrap(await api.GET("/knowledge/status")),
    refetchInterval: poll ? 3000 : false,
  });
}

/**
 * Whether an item is still on its way to being searchable.
 *
 * `indexing` belongs here and is easy to forget: the knowledge base takes
 * minutes to embed a document, and leaving it out freezes the screen
 * halfway through with no explanation.
 */
function isSettling(status: KnowledgeStatusValue): boolean {
  return (
    status === "pending" ||
    status === "queued" ||
    status === "processing" ||
    status === "indexing"
  );
}

/** The places one provider could send things — Slack's channels. Fetched
 *  only when the picker is open, because it calls Slack every time. */
export function useIntegrationDestinations(provider: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["integration-destinations", provider],
    enabled: provider !== null,
    queryFn: async () =>
      unwrap(
        await api.GET("/integrations/{provider}/destinations", {
          params: { path: { provider: provider ?? "" } },
        }),
      ),
  });
}

export function useIntegrations() {
  const api = useApi();
  return useQuery({
    queryKey: ["integrations"],
    queryFn: async () => unwrap(await api.GET("/integrations")),
  });
}

export function useInstall() {
  const api = useApi();
  return useQuery({
    queryKey: ["install"],
    queryFn: async () => unwrap(await api.GET("/install")),
  });
}

export function useLeads() {
  const api = useApi();
  return useQuery({
    queryKey: ["leads"],
    queryFn: async () => unwrap(await api.GET("/leads")),
  });
}

export function useConversations() {
  const api = useApi();
  return useQuery({
    queryKey: ["conversations"],
    queryFn: async () => unwrap(await api.GET("/conversations")),
  });
}

export function useConversation(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["conversations", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/conversations/{conversationId}", {
          params: { path: { conversationId: id } },
        }),
      ),
  });
}

/** A mutation that invalidates the given keys on success. */
export function useInvalidating<TArgs, TResult>(
  fn: (
    api: ReturnType<typeof useApi>,
    args: TArgs,
  ) => Promise<{ data?: TResult; error?: unknown }>,
  keys: string[][],
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: TArgs) => unwrap(await fn(api, args)),
    onSuccess: () => {
      for (const k of keys) void qc.invalidateQueries({ queryKey: k });
    },
  });
}
